import { ref } from 'vue';
import { useAlert } from 'dashboard/composables';

/**
 * A CONFIRMAÇÃO DE UMA AÇÃO QUE NÃO TEM VOLTA.
 *
 * Por que existe (14/09/2026): o Pipeline pedia confirmação com
 * `window.confirm()` em doze lugares — excluir webhook, excluir atividade,
 * excluir anexo, cancelar sequência, desvincular conversa, sobrescrever
 * filtro salvo. A caixa cinza do navegador tem a fonte do sistema, os botões
 * do sistema e o texto do sistema: "OK" e "Cancelar" para uma exclusão que
 * não volta. E no telefone ela vem do jeito que o navegador quiser.
 *
 * O Victor já tinha apontado exatamente isto em 29/07/2026 na lista de Casos.
 * A saída lá foi o `Dialog type="alert"` do produto — e este composable é
 * aquele mesmo arranjo tirado de dentro do arquivo, porque ele estava sendo
 * recopiado tela a tela (25 linhas por tela) e cada cópia divergia um pouco.
 *
 * Uso:
 *
 *   const { dialogo, textos, pedirConfirmacao, confirmar, confirmando } =
 *     useConfirmacao();
 *   pedirConfirmacao(() => api.destroy(id), {
 *     titulo: t('…'),
 *     descricao: t('…', { nome: webhook.name }),
 *     rotulo: t('…'),
 *   });
 *   …
 *   <Dialog ref="dialogo" type="alert" :title="textos.titulo"
 *           :description="textos.descricao"
 *           :confirm-button-label="textos.rotulo"
 *           :is-loading="confirmando" @confirm="confirmar" />
 *
 * A ação chega como função e só roda quando a pessoa confirma. Se ela falhar,
 * o alerta aparece: sem isso a ação confirmada falhava calada — o diálogo
 * parava de girar e ficava aberto, e ninguém sabia se tinha ido ou não.
 */
export function useConfirmacao(mensagemDeErro = null) {
  const { showAlert } = useAlert();
  const dialogo = ref(null);
  const acao = ref(null);
  const confirmando = ref(false);
  // O texto vem de QUEM pede: "excluir o webhook Cobrança" diz mais que
  // "tem certeza?", e é a diferença entre confirmar sem ler e confirmar
  // sabendo o que some.
  const textos = ref({ titulo: '', descricao: '', rotulo: '' });

  const pedirConfirmacao = (aFazer, dizeres = {}) => {
    acao.value = aFazer;
    textos.value = {
      titulo: dizeres.titulo || '',
      descricao: dizeres.descricao || '',
      rotulo: dizeres.rotulo || '',
    };
    dialogo.value?.open();
  };

  const confirmar = async () => {
    if (!acao.value) return;
    confirmando.value = true;
    try {
      await acao.value();
      dialogo.value?.close();
    } catch (e) {
      showAlert(
        e?.response?.data?.message ||
          mensagemDeErro ||
          'Não consegui concluir. Tente de novo.'
      );
    } finally {
      confirmando.value = false;
      acao.value = null;
    }
  };

  return { dialogo, textos, pedirConfirmacao, confirmar, confirmando };
}
